import { useEffect } from "react";
import ObjectUtils, { coalesce } from "../../../utils/ObjectUtils";
import { isVisible, visibleCollection } from "../../../utils/TriggerUtil";

import PropTypes from "prop-types";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Overview from "./Overview";
import Page from "./Page";

const { findObjectIndexInArray, getDeepProperty, opt } = ObjectUtils;

/**
 * The component represents SurveyView.
 */
const SurveyView = ({
  surveyName,
  surveyFundingArea,
  surveyProgram,
  surveyProject,
  surveyTemplate,
  triggerState,
  surveyCurrentPage,
  jumpToPage,
  showHideOverview,
  showOverviewMenu,
  readonly,
  readOnlyByPermissions,
  surveyResult,
  questionValidations,
  setQuestionAnswer,
  addQuestionAnswer,
  removeQuestionAnswer,
  endLoading,
  beginLoading,
  roles,
  addRow,
  importRows,
  deleteRow,
  deleteRows,
  addOtherQuestionAnswer,
  removeOtherQuestionAnswer,
  uploadFiles,
  downloadFile,
  selectFiles,
  loadPredefinedList,
  predefinedList,
  reorderQuestionRow,
  loadPreviousPeriodQuestionAnswers,
  downloadCSVTemplate,
  validateQuestionAnswer,
  onPageChange
}) => {
  useEffect(() => {
    onPageChange?.();
  }, [onPageChange, surveyCurrentPage]);

  const visiblePages = visibleCollection(surveyTemplate.pages, triggerState);
  let currIndex = findObjectIndexInArray(visiblePages, "name", surveyCurrentPage);
  currIndex = currIndex >= 0 && currIndex < visiblePages.length ? currIndex : 0;

  const currPage = getDeepProperty(visiblePages, currIndex);
  const currentPage = coalesce(opt(currPage).name, "");
  const monitoringEditOnly =
    surveyFundingArea?.name === "Aktivitätenmonitoring" && surveyName === "Aktivitätenmonitoring 2021" && !readonly;
  const disabled = readonly || readOnlyByPermissions || monitoringEditOnly;

  const renderPage = (page, disabled, monitoringEditOnly) => {
    const isActivityMonitoring = surveyFundingArea?.name === "Aktivitätenmonitoring";
    return (
      page &&
      isVisible(page, triggerState) && (
        <Page
          key={`key-${page.key}`}
          pageTemplate={page}
          surveyResult={surveyResult}
          surveyTemplate={surveyTemplate}
          surveyProgram={surveyProgram}
          setQuestionAnswer={setQuestionAnswer}
          triggerState={triggerState}
          addQuestionAnswer={addQuestionAnswer}
          endLoading={endLoading}
          removeQuestionAnswer={removeQuestionAnswer}
          beginLoading={beginLoading}
          questionValidations={questionValidations}
          addRow={addRow}
          disabled={disabled}
          monitoringEditOnly={monitoringEditOnly}
          isActivityMonitoring={isActivityMonitoring}
          importRows={importRows}
          deleteRow={deleteRow}
          deleteRows={deleteRows}
          loadPreviousPeriodQuestionAnswers={loadPreviousPeriodQuestionAnswers}
          downloadCSVTemplate={downloadCSVTemplate}
          addOtherQuestionAnswer={addOtherQuestionAnswer}
          removeOtherQuestionAnswer={removeOtherQuestionAnswer}
          uploadFiles={uploadFiles}
          selectFiles={selectFiles}
          downloadFile={downloadFile}
          loadPredefinedList={loadPredefinedList}
          predefinedList={predefinedList}
          currPage={page}
          reorderQuestionRow={reorderQuestionRow}
          validateQuestionAnswer={validateQuestionAnswer}
          roles={roles}
        />
      )
    );
  };

  return (
    surveyTemplate && (
      <Row>
        <Col sm={3} lg={2}>
          <Overview
            surveyName={surveyName}
            surveyFundingArea={surveyFundingArea}
            surveyProgram={surveyProgram}
            surveyProject={surveyProject}
            surveyTemplate={surveyTemplate}
            surveyCurrentPage={currentPage}
            showOverviewMenu={showOverviewMenu}
            jumpToPage={jumpToPage}
            showHideOverview={showHideOverview}
            triggerState={triggerState}
          />
        </Col>
        <Col sm={9} lg={10} className="d-flex flex-column" style={{ gap: "2rem" }}>
          {renderPage(currPage, disabled, monitoringEditOnly)}
        </Col>
      </Row>
    )
  );
};

SurveyView.propTypes = {
  surveyName: PropTypes.string,
  surveyFundingArea: PropTypes.object,
  surveyProgramInstance: PropTypes.object,
  surveyProgram: PropTypes.object,
  surveyProject: PropTypes.object,
  surveyCurrentPage: PropTypes.string,
  surveyTemplate: PropTypes.object.isRequired,
  surveyResult: PropTypes.object.isRequired,
  questionValidations: PropTypes.object.isRequired,
  jumpToPage: PropTypes.func,
  showHideOverview: PropTypes.func,
  showOverviewMenu: PropTypes.bool,
  setQuestionAnswer: PropTypes.func,
  triggerState: PropTypes.array,
  roles: PropTypes.array,
  addRow: PropTypes.func,
  importRows: PropTypes.func,
  endLoading: PropTypes.func,
  beginLoading: PropTypes.func,
  deleteRow: PropTypes.func,
  deleteRows: PropTypes.func,
  addQuestionAnswer: PropTypes.func,
  removeQuestionAnswer: PropTypes.func,
  addOtherQuestionAnswer: PropTypes.func,
  removeOtherQuestionAnswer: PropTypes.func,
  uploadFiles: PropTypes.func,
  downloadFile: PropTypes.func,
  selectFiles: PropTypes.func,
  loadPredefinedList: PropTypes.func,
  reorderQuestionRow: PropTypes.func,
  predefinedList: PropTypes.func,
  readonly: PropTypes.bool,
  readOnlyByPermissions: PropTypes.bool,
  editingDisabled: PropTypes.bool,
  loadPreviousPeriodQuestionAnswers: PropTypes.func,
  validateQuestionAnswer: PropTypes.func
};

export default SurveyView;
